<template>
  <div class="party-ledger">
    <div class="d-flex flex-wrap align-items-center gap-3">
      <ReportChoices :choices="quickRanges" :active="activeRange" @select="applyRange" />
    </div>

    <div class="rp-toolbar">
      <div class="rp-filters" style="--rp-cols: 4">
        <div class="rp-filter">
          <label>From date</label>
          <input v-model="filters.date_from" type="date" class="form-control" :max="filters.date_to || today" />
        </div>
        <div class="rp-filter">
          <label>To date</label>
          <input v-model="filters.date_to" type="date" class="form-control" :min="filters.date_from" :max="today" />
        </div>
        <div class="rp-filter acc-select">
          <label>Party Type</label>
          <FormDropdown :model-value="filters.party_type" :options="partyTypes" placeholder="All (customers & suppliers)"
                        clearable @update:model-value="setPartyType" />
        </div>
        <div class="rp-filter acc-select">
          <label>{{ filters.party_type ? meta.label : 'Customer / Supplier' }}</label>
          <FormDropdown v-model="filters.party" :options="partyOptions" :disabled="!filters.party_type"
                        :placeholder="filters.party_type ? `All ${meta.plural}` : 'Choose a party type first'"
                        searchable clearable />
        </div>
        <div class="rp-filter rp-search">
          <label>Search</label>
          <div class="position-relative">
            <i class="ti ti-search rp-search-icon"></i>
            <input v-model="filters.search" type="text" class="form-control"
                   :placeholder="isStatement ? 'Voucher, item or narration' : 'Search name or phone'" />
          </div>
        </div>
      </div>

      <ReportActions :loading="loading" :exporting="exporting" :can-reset="hasActiveFilters" :can-print="!!rows.length"
                     @reset="resetFilters" @export="exportFile" @print="printReport" />
    </div>

    <div v-if="error" class="alert alert-danger d-flex align-items-center gap-2">
      <i class="ti ti-alert-circle"></i>{{ error }}
    </div>

    <!-- Every party -->
    <template v-if="!isStatement">
      <div class="rp-cards" :class="{ 'is-loading': loading }">
        <component :is="card.show ? 'button' : 'div'" v-for="card in summaryCards" :key="card.key"
                   :type="card.show ? 'button' : undefined" class="rp-card" :class="[`tone-${card.tone}`, {
                     'rp-card-action': card.show, selected: card.show && filters.show === card.show }]"
                   :title="card.show ? (filters.show === card.show ? 'Show every party' : `Show only: ${card.label}`) : undefined"
                   @click="card.show && toggleShow(card.show)">
          <span class="rp-card-icon"><i class="ti" :class="card.icon"></i></span>
          <div class="min-w-0">
            <div class="rp-card-label">{{ card.label }}</div>
            <div class="rp-card-value" :class="card.valueClass">{{ formatMoney(card.value) }}</div>
            <div class="rp-card-sub">{{ card.sub }}</div>
          </div>
        </component>
      </div>

      <div class="rp-table-card">
        <div class="rp-table-head">
          <div class="rp-pills">
            <button v-for="pill in showPills" :key="pill.key" type="button" class="rp-pill"
                    :class="{ active: (filters.show || 'all') === pill.key }" @click="setShow(pill.key)">
              {{ pill.label }} <span class="rp-pill-count">{{ formatInt(pill.count) }}</span>
            </button>
          </div>
          <div class="d-flex align-items-center gap-2 small text-muted">
            Show
            <select v-model.number="pageSize" class="form-select form-select-sm rp-page-size">
              <option v-for="size in PAGE_SIZES" :key="size" :value="size">{{ size }}</option>
            </select>
            per page
          </div>
        </div>

        <div class="table-responsive rp-table-wrap">
          <div v-if="loading" class="rp-loading"><span class="spinner-border text-warning"></span></div>
          <table class="table rp-table mb-0">
            <thead>
              <tr>
                <th v-for="column in columns" :key="column.key" :class="column.class" @click="sortBy(column.key)">
                  <span class="rp-sortable" :class="{ active: sortKey === column.key }">
                    {{ column.label }}<i class="ti" :class="sortIcon(column.key)"></i>
                  </span>
                </th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in pagedRows" :key="row.key">
                <td>
                  <div class="fw-medium">{{ row.party }}</div>
                  <span v-if="row.phone" class="rp-sub">{{ row.phone }}</span>
                </td>
                <td v-if="!filters.party_type">
                  <span class="rp-badge" :class="typeMeta(row.party_type).tone">{{ row.type_label }}</span>
                </td>
                <td class="text-end acc-balance text-muted">{{ dueText(row.opening) }}</td>
                <td class="text-end" :class="{ 'text-muted': !Number(row.bill) }">{{ amountText(row.bill) }}</td>
                <td class="text-end" :class="{ 'text-muted': !Number(row.returns) }">{{ amountText(row.returns) }}</td>
                <td class="text-end" :class="{ 'text-muted': !Number(row.paid) }">{{ amountText(row.paid) }}</td>
                <td class="text-end fw-semibold acc-balance" :class="statusClass(row.status)">
                  {{ dueText(row.closing) }}
                  <span class="rp-sub">{{ statusLabel(row.party_type, row.status) }}</span>
                </td>
                <td class="text-nowrap">
                  {{ formatDate(row.last_date) }}
                  <span v-if="row.count" class="rp-sub">{{ plural(row.count, 'transaction') }}</span>
                </td>
                <td class="text-end">
                  <button v-if="row.party_id" type="button" class="btn btn-sm btn-outline-secondary text-nowrap"
                          @click="openStatement(row)">
                    <i class="ti ti-file-text me-1"></i>Statement
                  </button>
                </td>
              </tr>
              <tr v-if="!loading && !pagedRows.length">
                <td :colspan="columns.length + 1">
                  <div class="rp-empty">
                    <i class="ti ti-users"></i>
                    <div class="fw-semibold">No {{ meta.plural }} found</div>
                    <small class="text-muted">Try another period, filter or search.</small>
                  </div>
                </td>
              </tr>
            </tbody>
            <tfoot v-if="rows.length">
              <tr v-for="total in footerTotals" :key="total.type">
                <th class="text-end" :colspan="filters.party_type ? 1 : 2">
                  Total · {{ plural(total.party_count, total.single) }}
                </th>
                <th class="text-end acc-balance">{{ dueText(total.opening) }}</th>
                <th class="text-end">{{ formatMoney(total.bill) }}</th>
                <th class="text-end">{{ formatMoney(total.returns) }}</th>
                <th class="text-end">{{ formatMoney(total.paid) }}</th>
                <th class="text-end acc-balance">{{ dueText(total.closing) }}</th>
                <th colspan="2"></th>
              </tr>
            </tfoot>
          </table>
        </div>

        <ReportPagination v-if="visibleRows.length" :page="page" :page-count="pageCount" :pages="pageNumbers"
                          :start="pageStart" :end="pageEnd" :total="visibleRows.length" @go="goToPage" />
      </div>
    </template>

    <!-- One party's statement -->
    <template v-else>
      <div class="rp-cards" :class="{ 'is-loading': loading }">
        <div class="rp-card tone-navy">
          <span class="rp-card-icon"><i class="ti ti-user"></i></span>
          <div class="min-w-0">
            <div class="rp-card-label">{{ meta.label }}</div>
            <div class="rp-card-value text-truncate" :title="summary.party.name">{{ summary.party.name }}</div>
            <div class="rp-card-sub">{{ summary.party.phone || 'No phone' }} · opening due {{ dueText(summary.opening) }}</div>
          </div>
        </div>
        <div class="rp-card tone-green">
          <span class="rp-card-icon"><i class="ti ti-shopping-cart"></i></span>
          <div class="min-w-0">
            <div class="rp-card-label">{{ meta.bill }}</div>
            <div class="rp-card-value">{{ formatMoney(summary.bill) }}</div>
            <div class="rp-card-sub">
              {{ formatQty(summary.item_qty) }} {{ Number(summary.item_qty) === 1 ? 'item' : 'items' }}<template
                v-if="Number(summary.returns)"> · returns / credits {{ formatMoney(summary.returns) }}</template>
            </div>
          </div>
        </div>
        <div class="rp-card tone-orange">
          <span class="rp-card-icon"><i class="ti ti-cash"></i></span>
          <div class="min-w-0">
            <div class="rp-card-label">{{ meta.paid }}</div>
            <div class="rp-card-value">{{ formatMoney(summary.paid) }}</div>
            <div class="rp-card-sub">{{ plural(summary.voucher_count, 'transaction') }} in the period</div>
          </div>
        </div>
        <div class="rp-card" :class="summary.status === 'due' ? 'tone-red' : 'tone-teal'">
          <span class="rp-card-icon"><i class="ti ti-scale"></i></span>
          <div class="min-w-0">
            <div class="rp-card-label">{{ statusTitle(summary.party_type, summary.status) }}</div>
            <div class="rp-card-value" :class="{ 'text-danger': summary.status === 'due' }">
              {{ formatMoney(Math.abs(Number(summary.closing) || 0)) }}
            </div>
            <div class="rp-card-sub">closing due on {{ formatDate(filters.date_to) }}</div>
          </div>
        </div>
      </div>

      <div class="rp-table-card">
        <div class="rp-table-head">
          <div class="d-flex align-items-center gap-2 flex-wrap">
            <button type="button" class="btn btn-sm btn-outline-secondary" @click="filters.party = null">
              <i class="ti ti-arrow-left me-1"></i>All {{ meta.plural }}
            </button>
            <span class="fw-semibold">{{ summary.party.name }}</span>
            <span class="text-muted small">· {{ plural(rows.length, 'transaction') }}</span>
          </div>
          <div class="d-flex align-items-center gap-2 small text-muted">
            Show
            <select v-model.number="pageSize" class="form-select form-select-sm rp-page-size">
              <option v-for="size in PAGE_SIZES" :key="size" :value="size">{{ size }}</option>
            </select>
            per page
          </div>
        </div>

        <div class="table-responsive rp-table-wrap">
          <div v-if="loading" class="rp-loading"><span class="spinner-border text-warning"></span></div>
          <table class="table rp-table mb-0">
            <thead>
              <tr>
                <th>Date</th>
                <th>Voucher</th>
                <th>Items / Details</th>
                <th class="text-end">{{ meta.bill }}</th>
                <th class="text-end">{{ RETURNS }}</th>
                <th class="text-end">{{ meta.paid }}</th>
                <th class="text-end">Due</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="page === 1 && !loading" class="acc-opening">
                <td>{{ formatDate(filters.date_from) }}</td>
                <td></td>
                <td>Opening due</td>
                <td></td>
                <td></td>
                <td></td>
                <td class="text-end acc-balance">{{ dueText(summary.opening) }}</td>
              </tr>
              <tr v-for="row in pagedRows" :key="row.key">
                <td class="text-nowrap">{{ formatDate(row.date) }}</td>
                <td>
                  <div class="fw-medium text-nowrap">{{ row.voucher_id }}</div>
                  <span class="rp-badge" :class="typeTone(row.type_key)">{{ row.type }}</span>
                </td>
                <td class="pl-items">
                  <template v-if="row.items.length">
                    <div v-for="item in shownItems(row)" :key="`${item.item_id}-${item.item_name}-${item.variant}`"
                         class="pl-item">
                      <span class="fw-medium">{{ item.item_name }}</span>
                      <span v-if="item.variant" class="text-muted"> ({{ item.variant }})</span>
                      <span class="text-muted text-nowrap">
                        · {{ formatQty(item.qty) }}{{ item.unit ? ` ${item.unit}` : '' }} × {{ formatMoney(item.unit_price) }}
                        = {{ formatMoney(item.amount) }}
                      </span>
                    </div>
                    <button v-if="row.items.length > ITEMS_SHOWN" type="button" class="btn btn-link btn-sm p-0"
                            @click="toggleRow(row.key)">
                      {{ expanded[row.key] ? 'Show less' : `+${row.items.length - ITEMS_SHOWN} more items` }}
                    </button>
                  </template>
                  <div v-else>{{ detailText(row) }}</div>
                  <span v-if="row.narration" class="rp-sub">{{ row.narration }}</span>
                </td>
                <td class="text-end" :class="{ 'text-muted': Number(row.bill) <= 0 }">
                  {{ Number(row.bill) > 0 ? formatMoney(row.bill) : '—' }}
                </td>
                <td class="text-end" :class="{ 'text-muted': Number(row.bill) >= 0 }">
                  {{ Number(row.bill) < 0 ? formatMoney(-row.bill) : '—' }}
                </td>
                <td class="text-end" :class="{ 'text-muted': !Number(row.paid) }">
                  {{ paidText(row.paid) }}
                  <span v-if="Number(row.paid) < 0" class="rp-sub">refund</span>
                </td>
                <td class="text-end fw-semibold acc-balance" :class="statusClass(dueStatus(row.balance))">
                  {{ dueText(row.balance) }}
                </td>
              </tr>
              <tr v-if="!loading && !pagedRows.length">
                <td colspan="7">
                  <div class="rp-empty">
                    <i class="ti ti-file-text"></i>
                    <div class="fw-semibold">No transactions in the period</div>
                    <small class="text-muted">Try another period or search.</small>
                  </div>
                </td>
              </tr>
            </tbody>
            <tfoot v-if="!loading">
              <tr>
                <th colspan="3" class="text-end">Total · closing due ({{ statusTitle(summary.party_type, summary.status).toLowerCase() }})</th>
                <th class="text-end">{{ formatMoney(summary.bill) }}</th>
                <th class="text-end">{{ formatMoney(summary.returns) }}</th>
                <th class="text-end">{{ formatMoney(summary.paid) }}</th>
                <th class="text-end acc-balance">{{ dueText(summary.closing) }}</th>
              </tr>
            </tfoot>
          </table>
        </div>

        <ReportPagination v-if="rows.length" :page="page" :page-count="pageCount" :pages="pageNumbers"
                          :start="pageStart" :end="pageEnd" :total="rows.length" @go="goToPage" />
      </div>
    </template>

    <p class="rp-hint mt-2 mb-0">
      Every sale, purchase, return and payment with the party ({{ options.store || 'the store' }}, approved postings).
      A return or credit increases the due, and money received or paid reduces it. A customer's due is what they owe
      you, a supplier's what you owe them, and a negative due ("adv") is an advance. Dues are kept on {{ headsText }}.
    </p>
  </div>
</template>

<script>
import apiUrls from '@/urls';
import inventoryReport from '../inventory/inventoryReportMixin';
import periodReport, { defaultPeriod } from '../inventory/periodReportMixin';
import accountingReport from './accountingReportMixin';

const PARTY_META = {
  customer: {
    label: 'Customer', single: 'customer', plural: 'customers', due: 'Receivable', advance: 'Advance received',
    bill: 'Sales', paid: 'Received', tone: 'success',
  },
  supplier: {
    label: 'Supplier', single: 'supplier', plural: 'suppliers', due: 'Payable', advance: 'Advance paid',
    bill: 'Purchases', paid: 'Paid', tone: 'info',
  },
};
const ALL_META = {
  label: 'Party', single: 'party', plural: 'parties', due: 'Receivable / Payable', advance: 'Advance',
  bill: 'Sales / Purchases', paid: 'Received / Paid', tone: 'muted',
};
const ZERO_TOTALS = {
  opening: 0, bill: 0, returns: 0, paid: 0, closing: 0, due: 0, advance: 0, due_count: 0, advance_count: 0,
  party_count: 0, count: 0,
};
const EMPTY_SUMMARY = {
  party_type: 'all', heads: {}, by_type: {}, party_count: 0, show_counts: { all: 0, due: 0, advance: 0, active: 0 },
  party: { id: null, name: '—', phone: '', type: '' }, head: '—', opening: 0, bill: 0, returns: 0, paid: 0,
  closing: 0, due: 0, advance: 0, status: 'settled', item_qty: 0, voucher_count: 0, last_date: null,
};
const ITEMS_SHOWN = 3;
const RETURNS = 'Returns / Credits';

export default {
  name: 'PartyLedgerReport',
  mixins: [inventoryReport, periodReport, accountingReport],

  reportUrl: apiUrls.party_ledger_report.get,
  fileName: 'party_ledger',

  data() {
    return {
      filters: { ...defaultPeriod(), party_type: null, party: null, show: null, search: '' },
      summary: { ...EMPTY_SUMMARY },
      mode: 'summary',
      sortKey: null,
      sortDir: 'desc',
      ITEMS_SHOWN,
      RETURNS,
    };
  },

  computed: {
    meta() {
      return this.typeMeta(this.isStatement ? this.summary.party_type : this.filters.party_type);
    },

    columns() {
      const type = this.filters.party_type;
      return [
        { key: 'party', label: type ? this.meta.label : 'Party' },
        ...(type ? [] : [{ key: 'party_type', label: 'Type' }]),
        { key: 'opening', label: 'Opening Due', class: 'text-end', numeric: true },
        { key: 'bill', label: this.meta.bill, class: 'text-end', numeric: true },
        { key: 'returns', label: RETURNS, class: 'text-end', numeric: true },
        { key: 'paid', label: this.meta.paid, class: 'text-end', numeric: true },
        { key: 'closing', label: type ? this.meta.due : 'Closing Due', class: 'text-end', numeric: true },
        { key: 'last_date', label: 'Last Entry' },
      ];
    },

    partyTypes() {
      return Object.entries(PARTY_META).map(([id, meta]) => ({ id, text: meta.label }));
    },

    isStatement() {
      return this.mode === 'statement';
    },

    partyOptions() {
      return (this.options.parties || []).map((p) => {
        const parts = [p.name];
        if (p.phone) parts.push(p.phone);
        const bal = Number(p.balance) || 0;
        if (Math.abs(bal) > 0.005) parts.push(`Due ${this.formatMoney(Math.abs(bal))}`);
        return { id: p.id, text: parts.join(' · ') };
      });
    },

    hasActiveFilters() {
      const { party_type: type, party, show, search } = this.filters;
      return this.periodChanged || !!(type || party || show || search.trim());
    },

    totals() {
      const byType = this.summary.by_type || {};
      return {
        customer: { ...ZERO_TOTALS, ...(byType.customer || {}) },
        supplier: { ...ZERO_TOTALS, ...(byType.supplier || {}) },
      };
    },

    summaryCards() {
      const { customer: c, supplier: s } = this.totals;
      const type = this.filters.party_type;
      if (!type) {
        return [
          { key: 'receivable', label: 'Receivable', value: c.due, tone: 'red', icon: 'ti-clock-dollar', valueClass: 'text-danger',
            sub: `${this.plural(c.due_count, 'customer')} owe you` },
          { key: 'payable', label: 'Payable', value: s.due, tone: 'orange', icon: 'ti-receipt',
            sub: `you owe ${this.plural(s.due_count, 'supplier')}` },
          { key: 'sales', label: 'Sales', value: c.bill, tone: 'green', icon: 'ti-shopping-cart',
            sub: `returns / credits ${this.formatMoney(c.returns)} · received ${this.formatMoney(c.paid)}` },
          { key: 'purchases', label: 'Purchases', value: s.bill, tone: 'teal', icon: 'ti-truck',
            sub: `returns / credits ${this.formatMoney(s.returns)} · paid ${this.formatMoney(s.paid)}` },
        ];
      }
      const t = this.totals[type];
      const meta = this.meta;
      return [
        { key: 'due', label: meta.due, value: t.due, tone: 'red', icon: 'ti-clock-dollar', valueClass: 'text-danger',
          show: 'due', sub: `${this.plural(t.due_count, meta.single)} on ${this.formatDate(this.filters.date_to)}` },
        { key: 'advance', label: meta.advance, value: t.advance, tone: 'teal', icon: 'ti-coin', show: 'advance',
          sub: this.plural(t.advance_count, meta.single) },
        { key: 'bill', label: meta.bill, value: t.bill, tone: 'green', icon: type === 'customer' ? 'ti-shopping-cart' : 'ti-truck',
          sub: `returns / credits ${this.formatMoney(t.returns)}` },
        { key: 'paid', label: meta.paid, value: t.paid, tone: 'orange', icon: 'ti-cash',
          sub: `${this.plural(t.count, 'transaction')} in the period` },
      ];
    },

    footerTotals() {
      const types = this.filters.party_type ? [this.filters.party_type] : Object.keys(PARTY_META);
      return types
        .map((type) => ({ type, ...this.totals[type], single: PARTY_META[type].single }))
        .filter((t) => this.filters.party_type || t.party_count);
    },

    showPills() {
      const counts = this.summary.show_counts || {};
      return [
        { key: 'all', label: `All ${this.meta.plural}`, count: counts.all },
        { key: 'due', label: this.meta.due, count: counts.due },
        { key: 'advance', label: 'Advance', count: counts.advance },
        { key: 'active', label: 'Active in period', count: counts.active },
      ];
    },

    headsText() {
      if (this.isStatement) return `the ${this.summary.head} account`;
      const heads = Object.values(this.summary.heads || {});
      return heads.length ? `the ${heads.join(' and ')} account${heads.length > 1 ? 's' : ''}` : 'the party accounts';
    },

    visibleRows() {
      return this.sortKey && !this.isStatement ? this.sortRows(this.rows) : this.rows;
    },
  },

  watch: {
    'filters.party'() { this.onFilterChange(); },
    'filters.show'() { this.onFilterChange(); },
  },

  methods: {
    typeMeta(type) {
      return PARTY_META[type] || ALL_META;
    },

    extraParams() {
      const { party_type: type, party, show } = this.filters;
      return {
        ...this.periodParams(),
        ...(type ? { party_type: type } : {}),
        ...(type && party ? { party } : {}),
        ...(show && !party ? { show } : {}),
      };
    },

    resetExtraFilters() {
      this.sortKey = null;
      return { ...defaultPeriod(this.today), party_type: null, party: null, show: null };
    },

    applyResult(data) {
      this.mode = data?.mode || 'summary';
      this.summary = { ...EMPTY_SUMMARY, ...(data?.summary || {}) };
    },

    changeFilters(values) {
      this.applyingFilters = true;
      Object.assign(this.filters, values);
      this.sortKey = null;
      this.$nextTick(() => {
        this.applyingFilters = false;
        this.onFilterChange();
      });
    },

    setPartyType(type) {
      const value = PARTY_META[type] ? type : null;
      if (value === this.filters.party_type) return;
      this.changeFilters({ party_type: value, party: null, show: null });
    },

    openStatement(row) {
      this.changeFilters({ party_type: row.party_type, party: row.party_id });
    },

    setShow(key) {
      this.filters.show = key === 'all' ? null : key;
    },

    toggleShow(key) {
      this.filters.show = this.filters.show === key ? null : key;
    },

    dueStatus(value) {
      const n = Number(value) || 0;
      return n > 0.005 ? 'due' : n < -0.005 ? 'advance' : 'settled';
    },

    dueText(value) {
      const n = Number(value) || 0;
      if (Math.abs(n) < 0.005) return '0.00';
      return n > 0 ? this.formatMoney(n) : `${this.formatMoney(-n)} adv`;
    },

    paidText(value) {
      const n = Number(value) || 0;
      if (Math.abs(n) < 0.005) return '—';
      return n > 0 ? this.formatMoney(n) : `−${this.formatMoney(-n)}`;
    },

    statusClass(status) {
      return status === 'due' ? 'text-danger' : status === 'advance' ? 'text-success' : 'text-muted';
    },

    statusLabel(type, status) {
      const meta = this.typeMeta(type);
      return status === 'due' ? meta.due.toLowerCase() : status === 'advance' ? 'advance' : 'settled';
    },

    statusTitle(type, status) {
      const meta = this.typeMeta(type);
      return status === 'due' ? meta.due : status === 'advance' ? meta.advance : 'Settled';
    },

    shownItems(row) {
      return this.expanded[row.key] ? row.items : row.items.slice(0, ITEMS_SHOWN);
    },

    /** What a voucher without item lines did with the party: the account it was booked to, if any. */
    detailText(row) {
      if (row.heads?.length) return row.heads.map((h) => `${h.name} - ${h.id}`).join(', ');
      const customer = this.summary.party_type === 'customer';
      const bill = Number(row.bill) || 0;
      const paid = Number(row.paid) || 0;
      if (Math.abs(bill) < 0.005) {
        if (paid > 0) return customer ? 'Payment received against due' : 'Payment made against due';
        if (paid < 0) return customer ? 'Refund paid to customer' : 'Refund received from supplier';
        return row.type;
      }
      if (bill > 0) return customer ? 'Charged to the customer' : 'Bill from the supplier';
      return customer ? 'Credited to the customer' : 'Credit from the supplier';
    },

    itemsText(row) {
      return (row.items || []).map((i) => `${i.item_name}${i.variant ? ` (${i.variant})` : ''} × ${this.formatQty(i.qty)}`)
        .join('; ');
    },

    printReport() {
      const s = this.summary;
      const search = this.filters.search.trim();
      const meta = [this.periodText(), this.storeLabel(), search && `Search: ${search}`];
      if (this.isStatement) {
        const m = this.meta;
        this.printStatement(
          `${m.label} Ledger — ${s.party.name}`,
          meta,
          [['Opening due', this.dueText(s.opening)], [m.bill, this.formatMoney(s.bill)],
            [RETURNS, this.formatMoney(s.returns)], [m.paid, this.formatMoney(s.paid)],
            [this.statusTitle(s.party_type, s.status), this.formatMoney(Math.abs(Number(s.closing) || 0))]],
          [
            { label: 'Date', value: (r) => (r.date ? this.formatDate(r.date) : '') },
            { label: 'Voucher', value: (r) => r.voucher_id || '' },
            { label: 'Type', value: (r) => r.type || '' },
            { label: 'Items / Details', value: (r) => r._label || this.itemsText(r) || this.detailText(r) },
            { label: 'Narration', value: (r) => r.narration || '' },
            { label: m.bill, value: (r) => (Number(r.bill) > 0 ? this.formatMoney(r.bill) : ''), num: true },
            { label: RETURNS, value: (r) => (Number(r.bill) < 0 ? this.formatMoney(-r.bill) : ''), num: true },
            { label: m.paid, value: (r) => (r._label ? '' : this.paidText(r.paid)), num: true },
            { label: 'Due', value: (r) => this.dueText(r.balance), num: true },
          ],
          [{ _label: 'Opening due', balance: s.opening, _bold: true }, ...this.rows],
          { 'Items / Details': 'Closing due', [m.bill]: this.formatMoney(s.bill), [RETURNS]: this.formatMoney(s.returns),
            [m.paid]: this.formatMoney(s.paid), Due: this.dueText(s.closing) },
        );
        return;
      }
      const type = this.filters.party_type;
      const show = this.showPills.find((p) => p.key === (this.filters.show || 'all'))?.label;
      const totalsRow = type
        ? { Phone: 'Total', 'Opening Due': this.dueText(this.totals[type].opening), [this.meta.bill]: this.formatMoney(this.totals[type].bill),
          [RETURNS]: this.formatMoney(this.totals[type].returns), [this.meta.paid]: this.formatMoney(this.totals[type].paid),
          [this.meta.due]: this.dueText(this.totals[type].closing) }
        : null;
      this.printStatement(
        type ? `${this.meta.label} Ledger — all ${this.meta.plural}` : 'Party Ledger — customers & suppliers',
        [...meta, `Showing: ${show}`],
        this.summaryCards.map((card) => [card.label, `${this.formatMoney(card.value)} · ${card.sub}`]),
        [
          { label: type ? this.meta.label : 'Party', value: (r) => r.party },
          ...(type ? [] : [{ label: 'Type', value: (r) => r.type_label }]),
          { label: 'Phone', value: (r) => r.phone },
          { label: 'Opening Due', value: (r) => this.dueText(r.opening), num: true },
          { label: this.meta.bill, value: (r) => this.amountText(r.bill), num: true },
          { label: RETURNS, value: (r) => this.amountText(r.returns), num: true },
          { label: this.meta.paid, value: (r) => this.amountText(r.paid), num: true },
          { label: type ? this.meta.due : 'Closing Due', value: (r) => this.dueText(r.closing), num: true },
          { label: 'Status', value: (r) => this.statusLabel(r.party_type, r.status) },
          { label: 'Last Entry', value: (r) => (r.last_date ? this.formatDate(r.last_date) : '') },
        ],
        this.visibleRows,
        totalsRow,
      );
    },
  },
};
</script>

<style scoped src="../inventory/inventoryReport.css"></style>
<style scoped src="./accountingReport.css"></style>
<style scoped>
.pl-items {
  min-width: 260px;
}

.pl-item {
  line-height: 1.35;
}

.pl-item + .pl-item {
  margin-top: 2px;
}
</style>
